/**
 * Curated short labels + color categories for Replay Player upgrade chips.
 * Tooltip uses the original ParsedGame upgrade name.
 */

const LEVEL_RE = /^(.*)Level(\d+)$/;

/** Base name (race prefix stripped, no LevelN) → { short, category } */
const BASE_MAP = {
  InfantryWeapons: { short: 'IW', category: 'ground' },
  InfantryArmors: { short: 'IA', category: 'ground' },
  VehicleWeapons: { short: 'VW', category: 'mech' },
  VehicleArmors: { short: 'VA', category: 'mech' },
  ShipWeapons: { short: 'SW', category: 'mech' },
  ShipArmors: { short: 'SA', category: 'mech' },
  VehicleAndShipArmors: { short: 'VSA', category: 'mech' },
  VehicleAndShipPlating: { short: 'VSA', category: 'mech' },
  GroundWeapons: { short: 'GW', category: 'ground' },
  GroundArmors: { short: 'GA', category: 'ground' },
  Shields: { short: 'Sh', category: 'ground' },
  AirWeapons: { short: 'AW', category: 'mech' },
  AirArmors: { short: 'AA', category: 'mech' },
  MeleeWeapons: { short: 'ML', category: 'ground' },
  MissileWeapons: { short: 'MW', category: 'ground' },
  GroundMeleeWeapons: { short: 'ML', category: 'ground' },
  GroundMissileWeapons: { short: 'MW', category: 'ground' },
  FlyerAttacks: { short: 'FA', category: 'mech' },
  FlyerArmors: { short: 'FAr', category: 'mech' },
  FlyerCarapace: { short: 'FC', category: 'mech' },
  GroundCarapace: { short: 'GC', category: 'ground' },
  ZergMeleeWeapons: { short: 'ML', category: 'ground' },
  ZergMissileWeapons: { short: 'MW', category: 'ground' },
  ZergGroundArmors: { short: 'GC', category: 'ground' },
  ZergFlyerAttacks: { short: 'FA', category: 'mech' },
  ZergFlyerArmors: { short: 'FAr', category: 'mech' },
};

/** Exact upgrade name → { short, category } (abilities / one-offs) */
const EXACT_MAP = {
  Stimpack: { short: 'Stim', category: 'ability' },
  ShieldWall: { short: 'CS', category: 'ability' },
  CombatShield: { short: 'CS', category: 'ability' },
  PunisherGrenades: { short: 'Concuss', category: 'ability' },
  HiSecAutoTracking: { short: 'HiSec', category: 'ability' },
  BansheeCloak: { short: 'Cloak', category: 'ability' },
  PersonalCloaking: { short: 'Cloak', category: 'ability' },
  GhostCloak: { short: 'Cloak', category: 'ability' },
  RavenCorvidReactor: { short: 'Corvid', category: 'ability' },
  MedivacCaduceusReactor: { short: 'Caduceus', category: 'ability' },
  TerranBuildingArmor: { short: 'Bldg', category: 'ability' },
  NeosteelFrame: { short: 'Neo', category: 'ability' },
  InfestorEnergyUpgrade: { short: 'Pathog', category: 'ability' },
  zerglingmovementspeed: { short: 'Meta', category: 'ability' },
  zerglingattackspeed: { short: 'Adrenal', category: 'ability' },
  TunnelingClaws: { short: 'Tunnel', category: 'ability' },
  DiggingClaws: { short: 'Tunnel', category: 'ability' },
  Burrow: { short: 'Burrow', category: 'ability' },
  overlordspeed: { short: 'Pneumat', category: 'ability' },
  OverlordSpeed: { short: 'Pneumat', category: 'ability' },
  EvolveMuscularAugments: { short: 'Musc', category: 'ability' },
  GlialReconstitution: { short: 'Glial', category: 'ability' },
  CentrificalHooks: { short: 'Hooks', category: 'ability' },
  ChitinousPlating: { short: 'Chitin', category: 'ability' },
  AnabolicSynthesis: { short: 'Anabol', category: 'ability' },
  NeuralParasite: { short: 'Neural', category: 'ability' },
  Blink: { short: 'Blink', category: 'ability' },
  Charge: { short: 'Charge', category: 'ability' },
  WarpGateResearch: { short: 'WG', category: 'ability' },
  ExtendedThermalLance: { short: 'ETL', category: 'ability' },
  PsiStormTech: { short: 'Storm', category: 'ability' },
  PhoenixRangeUpgrade: { short: 'Anion', category: 'ability' },
  ObserverGraviticBooster: { short: 'Grav', category: 'ability' },
  GraviticDrive: { short: 'GravD', category: 'ability' },
  TempestGroundAttackUpgrade: { short: 'Tectonic', category: 'ability' },
};

function stripRacePrefix(name) {
  return String(name || '').replace(/^(Terran|Zerg|Protoss)/, '');
}

function fallbackShort(name) {
  // Opaque truncation only — ADR 0011 rejects algorithmic capital-letter abbreviations.
  const stripped = stripRacePrefix(name);
  return stripped.slice(0, 8) || '?';
}

function inferCategory(name) {
  const n = String(name || '').toLowerCase();
  if (
    n.includes('vehicle') ||
    n.includes('ship') ||
    n.includes('air') ||
    n.includes('flyer')
  ) {
    return 'mech';
  }
  if (
    n.includes('weapon') ||
    n.includes('armor') ||
    n.includes('shield') ||
    n.includes('carapace') ||
    n.includes('melee') ||
    n.includes('missile')
  ) {
    return 'ground';
  }
  return 'ability';
}

/**
 * @param {string} name Raw upgrade name from ParsedGame
 * @returns {{ short: string, full: string, category: 'ground'|'mech'|'ability' }}
 */
export function upgradeLabel(name) {
  const full = String(name || '');
  const exact = EXACT_MAP[full];
  if (exact) {
    return { short: exact.short, full, category: exact.category };
  }

  const levelMatch = full.match(LEVEL_RE);
  if (levelMatch) {
    const baseRaw = stripRacePrefix(levelMatch[1]);
    const level = levelMatch[2];
    const mapped = BASE_MAP[baseRaw];
    if (mapped) {
      return {
        short: `${mapped.short}+${level}`,
        full,
        category: mapped.category,
      };
    }
    return {
      short: `${fallbackShort(baseRaw)}+${level}`,
      full,
      category: inferCategory(full),
    };
  }

  const stripped = stripRacePrefix(full);
  const mappedBase = BASE_MAP[stripped];
  if (mappedBase) {
    return {
      short: mappedBase.short,
      full,
      category: mappedBase.category,
    };
  }

  return {
    short: fallbackShort(full),
    full,
    category: inferCategory(full),
  };
}
